import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCheckInputValuesParams,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowParameterValueErrorType
} from '../types'
import { SINGLE_OUTPUT_NAME, SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

const formats = ['CSV', 'JSON']
const options: Record<string, string[]> = {
  CSV: ['header = true', "field_delimiter = ','"],
  JSON: []
}
const extensions: Record<string, string> = {
  CSV: 'csv',
  JSON: 'json'
}

class ExportData extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.exportdata',
        group: 'Input/Output',
        title: 'Export to Bucket',
        description: `
      This components exports a table to a storage bucket.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'format',
          type: WorkflowComponentParamType.Selection,
          title: 'File format',
          default: formats[0],
          options: formats
        },
        {
          name: 'compressed',
          type: WorkflowComponentParamType.Boolean,
          title: 'Export compressed',
          default: true
        },
        {
          name: 'bucket',
          type: WorkflowComponentParamType.String,
          title: 'GCS Bucket name',
          placeholder: 'Bucket name',
          optional: false
        }
      ]
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getVersion(provider: Provider): string {
    return '2'
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    if (connection.type !== Provider.BigQuery) {
      return null
    }
    const preparedParams = this._prepareParameters(params)
    let query = ''
    const compressed = preparedParams.compressed as boolean
    const bucket = preparedParams.bucket
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const projectId = envVars.bigqueryProjectId
    const regionId = `region-${envVars.bigqueryRegion}`
    const format = params.format as string
    const extension = extensions[format]
    const compressionExtension = compressed ? '.gz' : ''
    const compressionOptions = compressed ? ["compression = 'GZIP'"] : []
    const commonOptions = [
      `uri = 'gs://%s/%s-*.${extension}${compressionExtension}'`,
      `format = '${format}'`,
      'overwrite = true'
    ]
    const exportOptions = commonOptions.concat(compressionOptions, options[format])

    const exportScript = dedent`
        DECLARE export_id STRING DEFAULT GENERATE_UUID();
        DECLARE bucket STRING DEFAULT '${bucket}';
        EXECUTE IMMEDIATE FORMAT('''
          EXPORT DATA -- carto-export-id: %s
            OPTIONS (
              ${exportOptions.join(',\n')}
            )
          AS (
            SELECT * FROM \`${preparedParams.source}\`
          )
         ''',
         export_id, bucket, export_id
         );
      `
    const resultQuery = `
        WITH export_result AS (
          SELECT timeline[0].pending_units + timeline[0].completed_units AS num_files
          FROM \`${projectId}\`.\`${regionId}\`.INFORMATION_SCHEMA.JOBS_BY_USER
          WHERE creation_time > TIMESTAMP_ADD(CURRENT_TIMESTAMP(), INTERVAL -1 HOUR)
            AND statement_type = 'EXPORT_DATA'
            AND query LIKE CONCAT('%', '-- carto-export-id: ', export_id, '%')
          LIMIT 1
        )
        SELECT
          i + 1 AS file_num,
          CONCAT(
            'https://storage.cloud.google.com/',
            bucket,
            '/',
            export_id,
            '-',
            LPAD(CAST(i AS STRING), 12, '0'),
            '.',
            '${extension}',
            '${compressionExtension}'
          ) AS url
        FROM export_result, UNNEST(GENERATE_ARRAY(0, num_files - 1)) AS i
        ORDER BY file_num
      `
    const resultCreationQuery = connection.getCreateTempTableQuery(outputTablename, resultQuery, codeType, null)

    query = connection.wrapCodeInIfTableExistsBlock(
      exportScript + '\n' + resultCreationQuery,
      [outputTablename],
      codeType
    )

    return {
      outputs: { [SINGLE_OUTPUT_NAME]: outputTablename },
      query: query
    }
  }

  checkInputValues({
    params,
    resolvedParamValues,
    connection,
    dryRun,
    schemas,
    variables
  }: WorkflowComponentCheckInputValuesParams) {
    const problems = super.checkInputValues({
      params,
      resolvedParamValues,
      connection,
      dryRun,
      schemas,
      variables
    })
    const preparedParams = this._prepareParameters(resolvedParamValues)
    // admit a valid bucket name optionally followed by a folder name
    // quotes and backslashes are not allowed in the folder name to avoid SQL syntax problems
    if (
      preparedParams.bucket &&
      !/^[a-z0-9][a-z0-9_.-]{1,61}[a-z0-9](\/[^'\\]+)?$/.test(preparedParams.bucket as string)
    ) {
      problems.push({
        parameter: 'bucket',
        type: WorkflowParameterValueErrorType.WrongValue,
        message: 'Invalid GCS bucket name'
      })
    }
    return problems
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema(): TableSchema {
    return [
      {
        name: 'file_num',
        type: ColumnDataType.Number
      },
      {
        name: 'url',
        type: ColumnDataType.String
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery]
  }
}

export { ExportData }
